import { Editor } from '@tiptap/react';
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Pilcrow,
  Redo2,
  Strikethrough,
  Undo2,
  Unlink,
} from 'lucide-react';
import { useState } from 'react';
import { Button, Select, Separator } from 'ui/components';
import { cn } from 'utils/cn';
import LinkInsertButton from './components/LinkInsertButton';
import { unlink } from './helpers';

type Level = 1 | 2 | 3;

export default function EditorToolbar({
  editor,
}: {
  editor: Editor | null;
}) {
  if (!editor) return null;
  const [openSnippetModal, setOpenSnippetModal] = useState(false);
  const [heading, setHeading] = useState<string>('paragraph');
  const isCursorOverLink = editor.getAttributes('link').href;

  const handleSelectChange = (value: string) => {
    switch (value) {
      case 'h1':
      case 'h2':
      case 'h3':
        const level = parseInt(value.slice(1)) as Level;
        editor.chain().focus().toggleHeading({ level }).run();
        break;
      case 'paragraph':
        editor.chain().focus().setParagraph().run();
        break;
      default:
        console.warn('EditorToolbar: Unhandled option');
    }
    setHeading(value);
  };

  editor.on('selectionUpdate', ({ editor }) => {
    if (editor.isActive('paragraph')) {
      setHeading('paragraph');
    }
    if (editor.isActive('heading', { level: 1 })) {
      setHeading('h1');
    }
    if (editor.isActive('heading', { level: 2 })) {
      setHeading('h2');
    }
    if (editor.isActive('heading', { level: 3 })) {
      setHeading('h3');
    }
  });

  const handleTemplateSubmit = (template: string) => {
    editor.commands.insertContent(template);
  };

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1 overflow-hidden rounded-tl-md rounded-tr-md border-l border-r border-input border-t p-1 md:h-12'
      )}
    >
      <Select
        onValueChange={handleSelectChange}
        defaultValue="paragraph"
        value={heading}
      >
        <Select.Trigger className="w-[140px]">
          <Select.Value placeholder="Format" />
        </Select.Trigger>
        <Select.Content>
          <Select.Group className="">
            <Select.Label>Format</Select.Label>
            <Select.Item className="flex text-gray-400" value="paragraph">
              <div className="flex items-center">
                <Pilcrow className="mr-2 h-4 w-4" />
                <span>Paragraph</span>
              </div>
            </Select.Item>
            <Select.Item className="flex text-gray-400" value="h1">
              <div className="flex items-center">
                <Heading1 className="mr-2 h-4 w-4" />
                <span>Heading 1</span>
              </div>
            </Select.Item>
            <Select.Item className="flex text-gray-400" value="h2">
              <div className="flex items-center">
                <Heading2 className="mr-2 h-4 w-4" />
                <span>Heading 2</span>
              </div>
            </Select.Item>
            <Select.Item className="flex text-gray-400" value="h3">
              <div className="flex items-center">
                <Heading3 className="mr-2 h-4 w-4" />
                <span>Heading 3</span>
              </div>
            </Select.Item>
          </Select.Group>
        </Select.Content>
      </Select>
      <Separator orientation="vertical" />
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'text-gray-600',
          editor.isActive('bold') && 'bg-gray-100'
        )}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'text-gray-600',
          editor.isActive('italic') && 'bg-gray-100'
        )}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'text-gray-600',
          editor.isActive('strike') && 'bg-gray-100'
        )}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough />
      </Button>
      <Separator orientation="vertical" />

      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'text-gray-600',
          editor.isActive('bulletList') && 'bg-gray-100'
        )}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'text-gray-600',
          editor.isActive('orderedList') && 'bg-gray-100'
        )}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'text-gray-600',
          editor.isActive('codeBlock') && 'bg-gray-100'
        )}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        <Code />
      </Button>

      <Separator orientation="vertical" />
      <LinkInsertButton editor={editor} />
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'text-gray-600',
          editor.isActive('link') && 'bg-gray-100'
        )}
        disabled={!isCursorOverLink}
        onClick={() => unlink(editor)}
      >
        <Unlink />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="text-gray-600"
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="text-gray-600"
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 />
      </Button>
    </div>
  );
}
